/**
 * Application Cases Data
 * Individual application case studies organized by category
 * Bilingual support (English/Korean)
 */

// Relative import (not the `@/` alias) so this module can also be imported
// from a Node/tsx context — the SEO/sitemap generator pulls it in via the
// route manifest, where Vite's path-alias resolver is not available.
import { BilingualText, BilingualArray, localizedText, localizedArray } from '../utils/i18n'

export interface BackgroundItemData {
  title: BilingualText
  items: BilingualArray
}

export interface ChallengeItemData {
  title: BilingualText
  items: BilingualArray
}

export interface CostComparisonRow {
  solution: string
  capex: string
  opex: string
  accu: string
  lcc: string
  isHighlighted?: boolean
}

export interface CostComparisonTableData {
  title?: BilingualText
  unit: string
  rows: CostComparisonRow[]
}

export interface TESMComparisonItemData {
  property: BilingualText
  glycerin: BilingualText
  moltenSalt: BilingualText
  isHighlighted?: boolean
}

export interface TESMComparisonTableData {
  title?: BilingualText
  items: TESMComparisonItemData[]
  conclusion?: BilingualText
}

export interface ESSComparisonRow {
  essType: string
  storageCapacity: string
  power: string
  energyDensity: string
  roundTripEfficiency: string
  lcos: string
  responseTime: string
  supplyEnergy: BilingualText
  isHighlighted?: boolean
}

export interface ESSComparisonTableData {
  title?: BilingualText
  rows: ESSComparisonRow[]
}

export interface BeforeAfterComparisonData {
  before: {
    src: string
    title: BilingualText
    citation?: {
      source: string
      url?: string
      reference?: string
    }
  }
  after: {
    src: string
    title: BilingualText
    caption?: BilingualText
  }
}

export interface SMRTESMRowData {
  smr: string
  reactorType: string
  heatSourceTemp: string
  tesm: BilingualText
}

export interface SMRTESMComparisonTableData {
  title?: BilingualText
  rows: SMRTESMRowData[]
  caption?: BilingualText
}

export interface ApplicationCaseData {
  id: string
  slug: string
  category: 'industrial' | 'residential' | 'grid' | 'smr'
  title: BilingualText
  shortTitle: BilingualText
  tagline: BilingualText
  description: BilingualText
  icon: string
  temperatureRange?: string
  keyFeatures: BilingualArray
  benefits: BilingualArray
  technicalHighlights?: BilingualArray
  relatedTechnology?: string[]
  background?: BackgroundItemData[]
  challenges?: ChallengeItemData[]
  solutionValues?: BilingualArray
  statusPlan?: BilingualArray
  images?: {
    before?: { src: string; caption: BilingualText }
    after?: { src: string; caption: BilingualText }
    diagram?: { src: string; caption: BilingualText }
    overview?: { src: string; caption: BilingualText }
  }
  merits?: BilingualArray
  costComparison?: CostComparisonTableData
  tesmComparison?: TESMComparisonTableData
  essComparison?: ESSComparisonTableData
  beforeAfterComparison?: BeforeAfterComparisonData
  smrTesmComparison?: SMRTESMComparisonTableData
  relatedProject?: {
    title: string
    href: string
  }
  comingSoon?: boolean
}

// Runtime interfaces (with localized strings)
export interface BackgroundItem {
  title: string
  items: string[]
}

export interface ChallengeItem {
  title: string
  items: string[]
}

export interface CostComparisonTable {
  title?: string
  unit: string
  rows: CostComparisonRow[]
}

export interface TESMComparisonItem {
  property: string
  glycerin: string
  moltenSalt: string
  isHighlighted?: boolean
}

export interface TESMComparisonTable {
  title?: string
  items: TESMComparisonItem[]
  conclusion?: string
}

export interface ESSComparisonRowRuntime {
  essType: string
  storageCapacity: string
  power: string
  energyDensity: string
  roundTripEfficiency: string
  lcos: string
  responseTime: string
  supplyEnergy: string
  isHighlighted?: boolean
}

export interface ESSComparisonTable {
  title?: string
  rows: ESSComparisonRowRuntime[]
}

export interface BeforeAfterComparison {
  before: {
    src: string
    title: string
    citation?: {
      source: string
      url?: string
      reference?: string
    }
  }
  after: {
    src: string
    title: string
    caption?: string
  }
}

export interface SMRTESMRow {
  smr: string
  reactorType: string
  heatSourceTemp: string
  tesm: string
}

export interface SMRTESMComparisonTable {
  title?: string
  rows: SMRTESMRow[]
  caption?: string
}

export interface ApplicationCase {
  id: string
  slug: string
  category: 'industrial' | 'residential' | 'grid' | 'smr'
  title: string
  shortTitle: string
  tagline: string
  description: string
  icon: string
  temperatureRange?: string
  keyFeatures: string[]
  benefits: string[]
  technicalHighlights?: string[]
  relatedTechnology?: string[]
  background?: BackgroundItem[]
  challenges?: ChallengeItem[]
  solutionValues?: string[]
  statusPlan?: string[]
  images?: {
    before?: { src: string; caption: string }
    after?: { src: string; caption: string }
    diagram?: { src: string; caption: string }
    overview?: { src: string; caption: string }
  }
  merits?: string[]
  costComparison?: CostComparisonTable
  tesmComparison?: TESMComparisonTable
  essComparison?: ESSComparisonTable
  beforeAfterComparison?: BeforeAfterComparison
  smrTesmComparison?: SMRTESMComparisonTable
  relatedProject?: {
    title: string
    href: string
  }
  comingSoon?: boolean
}

export interface ApplicationCategoryData {
  id: string
  slug: string
  title: BilingualText
  description: BilingualText
  icon: string
}

export interface ApplicationCategory {
  id: string
  slug: string
  title: string
  description: string
  icon: string
  cases: ApplicationCase[]
}

/**
 * All application cases organized by category (bilingual data)
 */
const applicationCasesData: ApplicationCaseData[] = [
  // ============================================
  // INDUSTRIAL APPLICATIONS
  // ============================================
  {
    id: 'aluminum-hot-air',
    slug: 'aluminum-hot-air',
    category: 'industrial',
    title: {
      en: 'Storing Waste Heat for Hot Combustion Air in Aluminum Processing',
      ko: '알루미늄 공정 폐열을 저장하여 연소 공기 예열에 활용'
    },
    shortTitle: {
      en: 'Aluminum Hot Combustion Air',
      ko: '알루미늄 연소 공기 예열'
    },
    tagline: {
      en: 'Capture waste heat to preheat combustion air and reduce fuel consumption',
      ko: '폐열을 회수하여 연소 공기를 예열하고 연료 소비를 절감'
    },
    description: {
      en: 'In aluminum processing, significant amounts of waste heat are generated during smelting and casting operations. IsoTES® captures this waste heat and stores it for later use in preheating combustion air for furnaces. By delivering hot air at consistent temperatures, this solution reduces natural gas consumption by up to 30% while improving overall energy efficiency and reducing carbon emissions.',
      ko: '알루미늄 가공 과정에서 용해 및 주조 작업 중 상당한 양의 폐열이 발생합니다. IsoTES®는 이 폐열을 포집하여 저장한 후, 용광로 연소 공기 예열에 활용합니다. 일정한 온도의 고온 공기를 공급함으로써 천연가스 소비를 최대 30% 절감하고, 전반적인 에너지 효율을 높이며 탄소 배출을 줄입니다.'
    },
    icon: '🏭',
    temperatureRange: '400-600°C',
    keyFeatures: {
      en: [
        'Waste heat capture from aluminum processing',
        'Constant-temperature hot air delivery',
        'Integration with existing furnace systems',
        'Multi-purpose output: hot air, cold energy, steam'
      ],
      ko: [
        '알루미늄 공정에서 폐열 회수',
        '일정 온도의 고온 공기 공급',
        '기존 용광로 시스템과 통합',
        '다목적 출력: 고온 공기, 냉열, 스팀'
      ]
    },
    benefits: {
      en: [
        '30% reduction in fuel costs',
        '30% reduction in CO2 emissions',
        'Additional CO2 reduction possible with renewable energy sources',
        'Lower CBAM-related carbon tax burden'
      ],
      ko: [
        '연료비 30% 절감',
        'CO2 배출 30% 감소',
        '재생에너지 활용 시 추가 CO2 감축 가능',
        'CBAM 관련 탄소세 부담 경감'
      ]
    },
    technicalHighlights: {
      en: [
        'High-temperature molten salt TESM',
        'Heat exchanger optimized for air heating',
        'Continuous operation capability'
      ],
      ko: [
        '고온 용융염 TESM',
        '공기 가열에 최적화된 열교환기',
        '연속 운전 가능'
      ]
    },
    relatedTechnology: ['HTS IsoTES®', 'TESM'],
    background: [
      {
        title: {
          en: 'Aluminum demand is rapidly increasing due to lightweighting trends',
          ko: '경량화 트렌드로 알루미늄 수요가 급증하고 있습니다'
        },
        items: {
          en: [
            'Aluminum is heavily used for battery packs and car skeletons',
            'EV manufacturers are driving unprecedented demand for lightweight materials'
          ],
          ko: [
            '배터리 팩과 차체 골격에 알루미늄이 대량 사용되고 있습니다',
            '전기차 제조업체들이 경량 소재에 대한 전례 없는 수요를 주도하고 있습니다'
          ]
        }
      },
      {
        title: {
          en: 'Large amounts of waste heat are currently being wasted',
          ko: '대량의 폐열이 현재 버려지고 있습니다'
        },
        items: {
          en: [
            'Significant thermal energy is vented to atmosphere during processing',
            'This represents both energy loss and environmental impact'
          ],
          ko: [
            '공정 중 상당한 열에너지가 대기 중으로 방출되고 있습니다',
            '이는 에너지 손실과 환경 영향을 모두 초래합니다'
          ]
        }
      }
    ],
    challenges: [
      {
        title: {
          en: 'EU CBAM (Carbon Border Adjustment Mechanism) poses significant challenges',
          ko: 'EU CBAM(탄소국경조정제도)이 큰 도전이 되고 있습니다'
        },
        items: {
          en: [
            'EU imposes carbon tax on imported aluminum based on CO2 content',
            'Korean aluminum products contain approximately 2x more CO2 than EU-produced aluminum',
            'This creates significant competitive disadvantage for Korean EV and aluminum exports'
          ],
          ko: [
            'EU는 CO2 함유량에 따라 수입 알루미늄에 탄소세를 부과합니다',
            '한국산 알루미늄은 EU산 대비 약 2배의 CO2를 함유하고 있습니다',
            '이로 인해 한국의 전기차 및 알루미늄 수출에 상당한 경쟁 열위가 발생합니다'
          ]
        }
      }
    ],
    solutionValues: {
      en: [
        'IsoTES® stores waste heat from aluminum processing that would otherwise be vented',
        'Stored heat can be used to generate hot combustion air for furnaces',
        'Additional applications include cold energy generation via absorption refrigerators',
        'Steam can be generated for neighboring facilities or processes',
        'This multi-purpose approach significantly contributes to CO2 emission reduction'
      ],
      ko: [
        'IsoTES®는 방출되는 알루미늄 공정 폐열을 저장합니다',
        '저장된 열은 용광로의 연소 공기 예열에 사용할 수 있습니다',
        '흡수식 냉동기를 통한 냉열 생산 등 추가 활용이 가능합니다',
        '인근 시설이나 공정에 스팀을 공급할 수 있습니다',
        '이러한 다목적 접근 방식은 CO2 배출 저감에 크게 기여합니다'
      ]
    },
    statusPlan: {
      en: [
        'This application is part of the Multi-Purpose Demonstration Facility Project',
        'Demonstration facility will validate waste heat recovery and multi-output capabilities',
        'Successful demonstration will pave the way for commercial-scale deployment in Korean aluminum industry'
      ],
      ko: [
        '이 응용 분야는 다목적 실증시설 프로젝트의 일부입니다',
        '실증시설에서 폐열 회수 및 다중 출력 기능을 검증할 예정입니다',
        '성공적인 실증은 한국 알루미늄 산업의 상업 규모 보급을 위한 기반이 될 것입니다'
      ]
    },
    images: {
      diagram: {
        src: '/images/applications/aluminum-processing-overview.webp',
        caption: {
          en: 'IsoTES® waste heat storage system for aluminum processing with multiple output applications',
          ko: '다중 출력 활용이 가능한 알루미늄 공정용 IsoTES® 폐열 저장 시스템'
        }
      }
    },
    merits: {
      en: [
        '30% Saving in Fuel Cost',
        '30% Reduction in CO2 Emission',
        'Additional reduction possible with renewable energy sources'
      ],
      ko: [
        '연료비 30% 절감',
        'CO2 배출 30% 감소',
        '재생에너지 활용 시 추가 감축 가능'
      ]
    }
  },
  {
    id: 'orc-absorption',
    slug: 'orc-absorption',
    category: 'industrial',
    title: {
      en: 'Low-Temperature Waste Heat to Electricity and Cooling',
      ko: '저온 폐열을 전기와 냉열로 변환'
    },
    shortTitle: {
      en: 'ORC Power & Absorption Cooling',
      ko: 'ORC 발전 및 흡수식 냉방'
    },
    tagline: {
      en: 'Convert low-grade waste heat into electricity and cooling energy',
      ko: '저급 폐열을 전기와 냉열로 변환'
    },
    description: {
      en: 'Many industrial processes generate low-temperature waste heat (typically below 250°C) that is difficult to utilize directly. IsoTES® stores this waste heat and transforms it into high-quality heat for Organic Rankine Cycle (ORC) generators for electricity production and absorption refrigerators for residential cooling.',
      ko: '많은 산업 공정에서 직접 활용하기 어려운 저온 폐열(통상 250°C 이하)이 발생합니다. IsoTES®는 이 폐열을 저장하고 ORC(유기 랭킨 사이클) 발전기용 고품질 열로 변환하여 전력을 생산하고, 흡수식 냉동기로 냉열을 생산합니다.'
    },
    icon: '⚡',
    temperatureRange: '80-250°C',
    keyFeatures: {
      en: [
        'Low-temperature waste heat capture and storage',
        'Transformation to high-quality heat with constant temperature',
        'ORC integration for power generation',
        'Absorption chiller integration for cold energy production'
      ],
      ko: [
        '저온 폐열 회수 및 저장',
        '일정 온도의 고품질 열로 변환',
        '전력 생산을 위한 ORC 통합',
        '냉열 생산을 위한 흡수식 냉동기 통합'
      ]
    },
    benefits: {
      en: [
        'Generate electricity from previously wasted heat',
        'Produce cold energy for residential cooling',
        'Effective for low-temperature and low-amount waste heat sources',
        'Improve overall plant energy efficiency'
      ],
      ko: [
        '버려지던 열에서 전기 생산',
        '주거용 냉방을 위한 냉열 생산',
        '저온·소량 폐열원에 효과적',
        '전반적인 공장 에너지 효율 향상'
      ]
    },
    technicalHighlights: {
      en: [
        'Glycerol-based TESM for optimal temperature range',
        'Dual output heat exchangers',
        'Smart load balancing between ORC and absorption systems'
      ],
      ko: [
        '최적 온도 범위를 위한 글리세롤 기반 TESM',
        '이중 출력 열교환기',
        'ORC와 흡수식 시스템 간 스마트 부하 균형'
      ]
    },
    relatedTechnology: ['IU IsoTES®', 'TESM'],
    background: [
      {
        title: {
          en: 'Numerous low-temperature waste heat sources exist',
          ko: '다양한 저온 폐열원이 존재합니다'
        },
        items: {
          en: [
            'Many industrial processes generate waste heat below 250°C',
            'This low-grade heat is often difficult to utilize directly'
          ],
          ko: [
            '많은 산업 공정에서 250°C 이하의 폐열이 발생합니다',
            '이 저급 열은 직접 활용하기 어려운 경우가 많습니다'
          ]
        }
      },
      {
        title: {
          en: 'Multiple utilization pathways available',
          ko: '다양한 활용 방안이 가능합니다'
        },
        items: {
          en: [
            'ORC (Organic Rankine Cycle) electricity generators can convert low-temperature heat to power',
            'Absorption refrigerators can utilize heat from IsoTES® to produce cold energy for residential cooling'
          ],
          ko: [
            'ORC(유기 랭킨 사이클) 발전기가 저온 열을 전력으로 변환할 수 있습니다',
            '흡수식 냉동기가 IsoTES®의 열을 활용하여 주거용 냉방용 냉열을 생산할 수 있습니다'
          ]
        }
      }
    ],
    challenges: [
      {
        title: {
          en: 'Upgrading waste heat to meet ORC requirements',
          ko: 'ORC 요구사항을 충족하기 위한 폐열 업그레이드'
        },
        items: {
          en: [
            'ORC generators require high-quality heat input',
            'Constant temperature is essential for efficient operation',
            'Steady flow is required for optimal power generation'
          ],
          ko: [
            'ORC 발전기는 고품질 열 입력이 필요합니다',
            '효율적인 운전을 위해 일정한 온도가 필수적입니다',
            '최적의 발전을 위해 안정적인 흐름이 필요합니다'
          ]
        }
      }
    ],
    solutionValues: {
      en: [
        'IsoTES® can store the waste heat and transform it into high-quality heat satisfying the requirements',
        'This solution is effective when the waste heat is relatively low-temperature and low-amount'
      ],
      ko: [
        'IsoTES®는 폐열을 저장하고 요구사항을 충족하는 고품질 열로 변환할 수 있습니다',
        '이 솔루션은 폐열이 상대적으로 저온이고 소량일 때 효과적입니다'
      ]
    },
    statusPlan: {
      en: [
        'This case is part of the Multi-Purpose Demonstration Facility Project',
        'Demonstration will validate low-temperature waste heat recovery capabilities'
      ],
      ko: [
        '이 사례는 다목적 실증시설 프로젝트의 일부입니다',
        '실증을 통해 저온 폐열 회수 기능을 검증할 예정입니다'
      ]
    },
    images: {
      diagram: {
        src: '/images/applications/orc-power-generator-overview.webp',
        caption: {
          en: 'IsoTES® low-temperature waste heat storage system for ORC power generation and absorption cooling',
          ko: 'ORC 발전 및 흡수식 냉방을 위한 IsoTES® 저온 폐열 저장 시스템'
        }
      }
    }
  },
  {
    id: 'grain-processing',
    slug: 'grain-processing',
    category: 'industrial',
    title: {
      en: 'Carbon-Free Steam for Grain Processing in Feedlots',
      ko: '사료공장 곡물 가공을 위한 무탄소 스팀'
    },
    shortTitle: {
      en: 'Feedlot Grain Processing',
      ko: '사료공장 곡물 가공'
    },
    tagline: {
      en: 'Renewable energy storage for steam generation in agricultural processing',
      ko: '농업 가공용 스팀 생산을 위한 재생에너지 저장'
    },
    description: {
      en: 'Feedlot operations require significant amounts of steam for grain processing, including flaking and steam rolling. IsoTES® stores renewable energy from solar or wind sources and delivers carbon-free steam on demand. This solution enables agricultural operations to eliminate fossil fuel boilers while maintaining consistent steam quality for optimal grain processing.',
      ko: '사료공장에서는 플레이킹, 스팀 롤링 등 곡물 가공에 상당한 양의 스팀이 필요합니다. IsoTES®는 태양광이나 풍력 등 재생에너지를 저장하고 필요시 무탄소 스팀을 공급합니다. 이 솔루션을 통해 농업 시설은 화석연료 보일러를 제거하면서도 최적의 곡물 가공을 위한 일정한 스팀 품질을 유지할 수 있습니다.'
    },
    icon: '🌾',
    temperatureRange: '150-200°C',
    keyFeatures: {
      en: [
        'Renewable energy integration (solar/wind)',
        'On-demand steam generation',
        'Consistent steam quality and pressure',
        'Grid-independent operation capability'
      ],
      ko: [
        '재생에너지 통합 (태양광/풍력)',
        '수요 맞춤 스팀 생산',
        '일정한 스팀 품질 및 압력',
        '계통 독립 운전 가능'
      ]
    },
    benefits: {
      en: [
        '100% carbon-free steam production',
        'Elimination of propane/natural gas boilers',
        'Reduced operational costs',
        'Enhanced sustainability credentials'
      ],
      ko: [
        '100% 무탄소 스팀 생산',
        '프로판/천연가스 보일러 제거',
        '운영비 절감',
        '지속가능성 인증 강화'
      ]
    },
    technicalHighlights: {
      en: [
        'Medium-temperature TESM selection',
        'Steam generator integration',
        'Renewable energy management system'
      ],
      ko: [
        '중온 TESM 선정',
        '스팀 발생기 통합',
        '재생에너지 관리 시스템'
      ]
    },
    relatedTechnology: ['Hybrid IsoTES®', 'TESM'],
    background: [
      {
        title: {
          en: 'Feedlots require steam for grain processing',
          ko: '사료공장은 곡물 가공에 스팀이 필요합니다'
        },
        items: {
          en: [
            'Grain flaking requires consistent high-quality steam',
            'Traditional steam generation relies on fossil fuels (propane, natural gas)',
            'Large feedlot operations have significant energy demands'
          ],
          ko: [
            '곡물 플레이킹에는 일정한 고품질 스팀이 필요합니다',
            '기존 스팀 생산은 화석연료(프로판, 천연가스)에 의존합니다',
            '대규모 사료공장은 상당한 에너지 수요가 있습니다'
          ]
        }
      },
      {
        title: {
          en: 'Renewable energy opportunities exist',
          ko: '재생에너지 기회가 존재합니다'
        },
        items: {
          en: [
            'Solar and wind resources are often abundant in agricultural regions',
            'Renewable energy can be cost-effective but requires storage for on-demand use'
          ],
          ko: [
            '농업 지역에는 태양광과 풍력 자원이 풍부한 경우가 많습니다',
            '재생에너지는 비용 효율적일 수 있지만 수요 맞춤 사용을 위해 저장이 필요합니다'
          ]
        }
      }
    ],
    challenges: [
      {
        title: {
          en: 'Decarbonizing agricultural steam generation',
          ko: '농업 분야 스팀 생산의 탈탄소화'
        },
        items: {
          en: [
            'Steam must be available on-demand regardless of renewable energy availability',
            'Consistent steam quality is critical for grain processing efficiency',
            'Transitioning from fossil fuels requires reliable alternative solutions'
          ],
          ko: [
            '재생에너지 가용 여부와 관계없이 스팀이 필요시 공급되어야 합니다',
            '일정한 스팀 품질은 곡물 가공 효율에 매우 중요합니다',
            '화석연료에서 전환하려면 신뢰할 수 있는 대안 솔루션이 필요합니다'
          ]
        }
      }
    ],
    solutionValues: {
      en: [
        'IsoTES® stores renewable energy and delivers carbon-free steam on demand',
        'Enables complete elimination of propane/natural gas boilers',
        'Maintains consistent steam quality for optimal grain processing',
        'Supports sustainability goals for agricultural operations'
      ],
      ko: [
        'IsoTES®는 재생에너지를 저장하고 필요시 무탄소 스팀을 공급합니다',
        '프로판/천연가스 보일러의 완전한 제거가 가능합니다',
        '최적의 곡물 가공을 위한 일정한 스팀 품질을 유지합니다',
        '농업 시설의 지속가능성 목표를 지원합니다'
      ]
    },
    statusPlan: {
      en: [
        'This application targets large-scale feedlot operations in regions with abundant renewable resources',
        'Pilot demonstrations are planned to validate steam generation performance'
      ],
      ko: [
        '이 응용 분야는 재생에너지가 풍부한 지역의 대규모 사료공장을 대상으로 합니다',
        '스팀 생산 성능을 검증하기 위한 파일럿 실증이 계획되어 있습니다'
      ]
    },
    images: {
      diagram: {
        src: '/images/applications/grain-process-for-feedlots-overview.webp',
        caption: {
          en: 'IsoTES® renewable energy storage system for carbon-free steam generation in feedlot grain processing',
          ko: '사료공장 곡물 가공용 무탄소 스팀 생산을 위한 IsoTES® 재생에너지 저장 시스템'
        }
      }
    },
    costComparison: {
      title: {
        en: 'Economic Comparison of Steam Generation Solutions',
        ko: '스팀 생산 솔루션 경제성 비교'
      },
      unit: 'MAUD',
      rows: [
        { solution: 'LNG Boiler', capex: '0.90', opex: '8.30', accu: '-', lcc: '9.20' },
        { solution: 'Grid Electricity', capex: '0.45', opex: '27.08', accu: '-', lcc: '27.53' },
        { solution: 'Solar Panel + Battery', capex: '9.00', opex: '2.70', accu: '-0.72', lcc: '10.98' },
        { solution: 'Solar Panel + H2', capex: '27.47', opex: '8.24', accu: '-0.72', lcc: '34.99' },
        { solution: 'Solar Panel + IsoTES®', capex: '7.56', opex: '2.27', accu: '-0.72', lcc: '9.11', isHighlighted: true }
      ]
    }
  },
  {
    id: 'lng-cold-energy',
    slug: 'lng-cold-energy',
    category: 'industrial',
    title: {
      en: 'LNG Cold Energy for CO2 Liquefaction',
      ko: 'CO2 액화를 위한 LNG 냉열 활용'
    },
    shortTitle: {
      en: 'LNG Cold Energy Utilization',
      ko: 'LNG 냉열 활용'
    },
    tagline: {
      en: 'Capture LNG regasification cold energy for carbon capture applications',
      ko: 'LNG 기화 냉열을 포집하여 탄소 포집에 활용'
    },
    description: {
      en: 'LNG regasification releases significant cold energy that is typically wasted. IsoTES® captures and stores this cold energy using specialized cryogenic storage materials, then delivers it for CO2 liquefaction in carbon capture applications. This innovative approach creates value from waste cold energy while supporting decarbonization efforts.',
      ko: 'LNG 기화 시 상당한 냉열이 방출되지만 대부분 버려지고 있습니다. IsoTES®는 특수 극저온 저장 물질을 사용하여 이 냉열을 포집 및 저장한 후, 탄소 포집 응용 분야에서 CO2 액화에 활용합니다. 이 혁신적인 접근법은 버려지는 냉열로부터 가치를 창출하고 탈탄소화를 지원합니다.'
    },
    icon: '❄️',
    temperatureRange: '-160 to -40°C',
    keyFeatures: {
      en: [
        'Cryogenic cold energy capture',
        'Specialized low-temperature storage',
        'CO2 liquefaction system integration',
        'Continuous cold supply capability'
      ],
      ko: [
        '극저온 냉열 포집',
        '특수 저온 저장',
        'CO2 액화 시스템 통합',
        '연속 냉열 공급 가능'
      ]
    },
    benefits: {
      en: [
        'Utilize otherwise wasted cold energy',
        'Reduce energy costs for CO2 liquefaction by 60%',
        'Enable efficient carbon capture and storage',
        'Create new revenue streams from LNG operations'
      ],
      ko: [
        '버려지던 냉열 활용',
        'CO2 액화 에너지 비용 60% 절감',
        '효율적인 탄소 포집 및 저장 가능',
        'LNG 운영에서 새로운 수익원 창출'
      ]
    },
    technicalHighlights: {
      en: [
        'Ethanol-based cryogenic TESM',
        'Insulated storage system design',
        'Heat exchanger optimized for CO2 phase change'
      ],
      ko: [
        '에탄올 기반 극저온 TESM',
        '단열 저장 시스템 설계',
        'CO2 상변화에 최적화된 열교환기'
      ]
    },
    relatedTechnology: ['IU IsoTES®', 'TESM'],
    background: [
      {
        title: {
          en: 'Significant Cold Energy Potential',
          ko: '상당한 냉열 잠재력'
        },
        items: {
          en: [
            'Some countries consume a huge amount of LNG. For example, Korea is consuming 127,000 ton or 280,000 m³ LNG every day',
            'As the temperature of LNG is around -160°C, there is substantial potential to use the cold energy from the consumed LNG',
            'For example, if the cold energy from the LNG that Korea is daily consuming is used for CO2 liquefaction, 250,000 ton CO₂ can be liquefied every day, corresponding to 14% of the Korea\'s total CO₂ emissions',
            'There are a number of applications of LNG cold energy utilization other than CO2 liquefaction'
          ],
          ko: [
            '일부 국가는 막대한 양의 LNG를 소비합니다. 예를 들어 한국은 매일 127,000톤 또는 280,000m³의 LNG를 소비하고 있습니다',
            'LNG의 온도가 약 -160°C이므로 소비되는 LNG에서 냉열을 활용할 수 있는 상당한 잠재력이 있습니다',
            '예를 들어 한국이 매일 소비하는 LNG의 냉열을 CO2 액화에 사용하면 매일 250,000톤의 CO2를 액화할 수 있으며, 이는 한국 전체 CO2 배출량의 14%에 해당합니다',
            'CO2 액화 외에도 LNG 냉열 활용의 다양한 응용 분야가 있습니다'
          ]
        }
      }
    ],
    challenges: [
      {
        title: {
          en: 'Load Balancing Difficulty',
          ko: '부하 균형의 어려움'
        },
        items: {
          en: [
            'However, the cold energy from LNG has not been utilized actively because it is not easy to balance the thermal loads between the cold energy production (LNG vaporization) and cold energy utilization (CO2 liquefaction)'
          ],
          ko: [
            '그러나 LNG 냉열은 냉열 생산(LNG 기화)과 냉열 활용(CO2 액화) 사이의 열 부하 균형을 맞추기 어려워 적극적으로 활용되지 못하고 있습니다'
          ]
        }
      }
    ],
    solutionValues: {
      en: [
        'The IsoTES® can store the cold energy from the LNG vaporization process and releases it to the CO2 liquefaction process, playing the cold energy buffer and separating the load change of the two processes',
        'Even in case one of the two process is shutdown, the other process can operate without any load change for a long period, typically 5 hours'
      ],
      ko: [
        'IsoTES®는 LNG 기화 공정에서 냉열을 저장하고 CO2 액화 공정에 방출하여 냉열 버퍼 역할을 하고 두 공정의 부하 변화를 분리합니다',
        '두 공정 중 하나가 정지되더라도 다른 공정은 통상 5시간 동안 부하 변화 없이 운전할 수 있습니다'
      ]
    },
    merits: {
      en: [
        'IsoTES® solves all challenges with a single-tank design',
        'Time mismatch between cold production and consumption',
        'Load fluctuation in cold production and consumption',
        'Temperature mismatch between the two systems',
        'Complex design and operation, and increased installation space'
      ],
      ko: [
        'IsoTES®는 단일 탱크 설계로 모든 과제를 해결합니다',
        '냉열 생산과 소비 간 시간 불일치',
        '냉열 생산과 소비의 부하 변동',
        '두 시스템 간 온도 불일치',
        '복잡한 설계 및 운영, 설치 공간 증가'
      ]
    },
    statusPlan: {
      en: [
        'GIGAette and KAIST is building a lab test facility with 1 ton/day CO2 liquefaction capacity. Here, liquid nitrogen (LN2) will be used to simulate LNG',
        'After this, a pilot project will be launched to utilize the cold energy from LNG'
      ],
      ko: [
        'GIGAette와 KAIST가 일 1톤 CO2 액화 용량의 실험실 테스트 시설을 구축 중입니다. 여기서 LNG를 모사하기 위해 액체 질소(LN2)를 사용합니다',
        '이후 LNG 냉열을 활용하기 위한 파일럿 프로젝트가 착수될 예정입니다'
      ]
    },
    images: {
      diagram: {
        src: '/images/applications/lng-cold-energy.webp',
        caption: {
          en: 'HTS-IsoTES® cold energy storage system for LNG vaporization and CO2 liquefaction',
          ko: 'LNG 기화 및 CO2 액화를 위한 HTS-IsoTES® 냉열 저장 시스템'
        }
      }
    }
  },
  {
    id: 'biomass-marine-fuel',
    slug: 'biomass-marine-fuel',
    category: 'industrial',
    title: {
      en: 'Carbon-Free Marine Fuel from Biomass Pyrolysis',
      ko: '바이오매스 열분해를 통한 무탄소 선박 연료'
    },
    shortTitle: {
      en: 'Biomass Marine Fuel',
      ko: '바이오매스 선박 연료'
    },
    tagline: {
      en: 'Thermal energy storage enabling continuous biomass pyrolysis for marine fuel production',
      ko: '해양 연료 생산을 위한 연속 바이오매스 열분해를 가능하게 하는 열에너지 저장'
    },
    description: {
      en: 'Biomass pyrolysis requires consistent high-temperature heat input for efficient conversion to marine fuels. IsoTES® stores renewable energy and delivers stable high-temperature heat to pyrolysis reactors, enabling 24/7 operation regardless of renewable energy intermittency. This solution supports the production of carbon-neutral marine fuels essential for shipping industry decarbonization.',
      ko: '바이오매스 열분해는 선박 연료로의 효율적인 전환을 위해 일정한 고온 열 공급이 필요합니다. IsoTES®는 재생에너지를 저장하고 열분해 반응기에 안정적인 고온 열을 공급하여 재생에너지의 간헐성과 무관하게 24시간 운전을 가능하게 합니다. 이 솔루션은 해운 산업 탈탄소화에 필수적인 탄소 중립 선박 연료 생산을 지원합니다.'
    },
    icon: '🚢',
    temperatureRange: '400-600°C',
    keyFeatures: {
      en: [
        'High-temperature heat storage for pyrolysis',
        'Continuous reactor operation',
        'Renewable energy integration',
        'Marine fuel quality optimization'
      ],
      ko: [
        '열분해를 위한 고온 열 저장',
        '연속 반응기 운전',
        '재생에너지 통합',
        '선박 연료 품질 최적화'
      ]
    },
    benefits: {
      en: [
        'Enable continuous pyrolysis operation',
        'Produce carbon-neutral marine fuels',
        'Support IMO decarbonization targets',
        'Reduce marine fuel production costs'
      ],
      ko: [
        '연속 열분해 운전 가능',
        '탄소 중립 선박 연료 생산',
        'IMO 탈탄소화 목표 지원',
        '선박 연료 생산 비용 절감'
      ]
    },
    technicalHighlights: {
      en: [
        'Molten salt TESM for high temperatures',
        'Pyrolysis reactor heat integration',
        'Process control for fuel quality'
      ],
      ko: [
        '고온용 용융염 TESM',
        '열분해 반응기 열 통합',
        '연료 품질을 위한 공정 제어'
      ]
    },
    relatedTechnology: ['HTS IsoTES®', 'TESM'],
    comingSoon: true
  },
  {
    id: 'saf-production',
    slug: 'saf-production',
    category: 'industrial',
    title: {
      en: 'Sustainable Aviation Fuel (SAF) Production',
      ko: '지속가능 항공연료(SAF) 생산'
    },
    shortTitle: {
      en: 'SAF Production',
      ko: 'SAF 생산'
    },
    tagline: {
      en: 'Thermal energy storage for biological SAF production processes',
      ko: '생물학적 SAF 생산 공정을 위한 열에너지 저장'
    },
    description: {
      en: 'Biological processes for SAF production require precise temperature control and consistent heat supply. IsoTES® provides stable thermal energy for fermentation, distillation, and other biological conversion processes. By storing renewable energy, this solution enables carbon-neutral SAF production that meets aviation industry sustainability requirements.',
      ko: 'SAF 생산을 위한 생물학적 공정은 정밀한 온도 제어와 일정한 열 공급이 필요합니다. IsoTES®는 발효, 증류 및 기타 생물학적 전환 공정에 안정적인 열에너지를 제공합니다. 재생에너지를 저장함으로써 이 솔루션은 항공 산업의 지속가능성 요구사항을 충족하는 탄소 중립 SAF 생산을 가능하게 합니다.'
    },
    icon: '✈️',
    temperatureRange: '60-180°C',
    keyFeatures: {
      en: [
        'Precise temperature control for biological processes',
        'Heat integration with fermentation systems',
        'Distillation process support',
        'Renewable energy utilization'
      ],
      ko: [
        '생물학적 공정을 위한 정밀 온도 제어',
        '발효 시스템과 열 통합',
        '증류 공정 지원',
        '재생에너지 활용'
      ]
    },
    benefits: {
      en: [
        'Enable 24/7 biological process operation',
        'Produce carbon-neutral aviation fuel',
        'Meet airline sustainability requirements',
        'Reduce SAF production costs'
      ],
      ko: [
        '24시간 생물학적 공정 운전 가능',
        '탄소 중립 항공 연료 생산',
        '항공사 지속가능성 요구사항 충족',
        'SAF 생산 비용 절감'
      ]
    },
    technicalHighlights: {
      en: [
        'Multi-temperature TESM system',
        'Process heat integration',
        'Biological process optimization'
      ],
      ko: [
        '다중 온도 TESM 시스템',
        '공정 열 통합',
        '생물학적 공정 최적화'
      ]
    },
    relatedTechnology: ['IU IsoTES®', 'Hybrid IsoTES®', 'TESM'],
    comingSoon: true
  },

  // ============================================
  // RESIDENTIAL APPLICATIONS
  // ============================================
  {
    id: 'residential-heating',
    slug: 'residential-heating',
    category: 'residential',
    title: {
      en: 'Storing Electricity as Heat for Residential Heating',
      ko: '주거용 난방을 위해 전기를 열로 저장'
    },
    shortTitle: {
      en: 'Residential Electric Heating',
      ko: '주거용 전기 난방'
    },
    tagline: {
      en: 'Replace coal-fired heating with clean, automated thermal energy storage',
      ko: '석탄 난방을 깨끗하고 자동화된 열에너지 저장으로 대체'
    },
    description: {
      en: 'IsoTES® transforms residential heating by storing electricity as thermal energy, eliminating the need for coal-fired boilers. This solution is particularly effective in regions like Mongolia where coal-based heating causes significant environmental and economic problems, while abundant solar PV potential remains underutilized during daytime hours.',
      ko: 'IsoTES®는 전기를 열에너지로 저장하여 석탄 보일러 없이 주거용 난방을 혁신합니다. 이 솔루션은 석탄 기반 난방이 심각한 환경 및 경제 문제를 일으키는 반면 풍부한 태양광 잠재력이 낮 시간 동안 활용되지 않는 몽골과 같은 지역에서 특히 효과적입니다.'
    },
    icon: '🏠',
    temperatureRange: '50-90°C',
    keyFeatures: {
      en: [
        'Store electricity from solar PV or grid during daytime',
        'Release heat for residential heating at nighttime',
        'Full automation with no manual operation',
        'Scalable from single buildings to communities'
      ],
      ko: [
        '낮 시간에 태양광 또는 계통에서 전기 저장',
        '야간에 주거 난방용 열 방출',
        '수동 운전 없이 완전 자동화',
        '단일 건물에서 커뮤니티까지 확장 가능'
      ]
    },
    benefits: {
      en: [
        'No coal (purchase, transport, waste, pollution, tax)',
        'Full automation - no 3-shift workers needed',
        'Negligible OPEX compared to coal boilers',
        'Applicable to similar regions worldwide'
      ],
      ko: [
        '석탄 불필요 (구매, 운송, 폐기물, 오염, 세금)',
        '완전 자동화 - 3교대 근로자 불필요',
        '석탄 보일러 대비 무시할 수준의 운영비',
        '전 세계 유사 지역에 적용 가능'
      ]
    },
    technicalHighlights: {
      en: [
        'Water-based TESM for safety',
        'Compact containerized design',
        'Smart grid integration'
      ],
      ko: [
        '안전을 위한 수성 TESM',
        '컴팩트한 컨테이너형 설계',
        '스마트 그리드 통합'
      ]
    },
    relatedTechnology: ['IU IsoTES®', 'TESM'],
    background: [
      {
        title: {
          en: 'Mongolia is one of the best places for solar PV',
          ko: '몽골은 태양광에 가장 좋은 지역 중 하나입니다'
        },
        items: {
          en: [
            'Excellent weather and geographical conditions for solar energy',
            'But solar is only available during daytime'
          ],
          ko: [
            '태양광에 우수한 기상 및 지리적 조건',
            '하지만 태양광은 낮 시간에만 가능합니다'
          ]
        }
      }
    ],
    challenges: [
      {
        title: {
          en: 'Coal-based residential heating causes multiple problems',
          ko: '석탄 기반 주거 난방은 여러 문제를 일으킵니다'
        },
        items: {
          en: [
            'Purchase and logistics cost of coal',
            'Waste handling',
            'Air pollution tax',
            'Salary for 3-shift workers in bad working conditions',
            'All costs increasing year on year'
          ],
          ko: [
            '석탄 구매 및 물류 비용',
            '폐기물 처리',
            '대기오염세',
            '열악한 근무 환경에서 3교대 근로자 급여',
            '모든 비용이 매년 증가'
          ]
        }
      }
    ],
    solutionValues: {
      en: [
        'IsoTES® stores electricity from solar PV in daytime and releases it as heat for residential heating at nighttime',
        'This solution is cost-effective, removing all problems caused by coal-fired residential heating',
        'Takes advantage of the abundant solar PV opportunities in Mongolia',
        'This concept can be applied to other areas with similar conditions'
      ],
      ko: [
        'IsoTES®는 낮에 태양광 전기를 저장하고 밤에 주거 난방용 열로 방출합니다',
        '이 솔루션은 비용 효율적이며 석탄 난방으로 인한 모든 문제를 제거합니다',
        '몽골의 풍부한 태양광 기회를 활용합니다',
        '이 개념은 유사한 조건의 다른 지역에도 적용될 수 있습니다'
      ]
    },
    statusPlan: {
      en: [
        'A 1.5 MWh demonstration project was successfully delivered in March, 2025',
        'The next project is launched in November, 2025 aiming at deployment of commercial scale IsoTES® for this purpose',
        'KOICA has been supporting both projects'
      ],
      ko: [
        '1.5 MWh 실증 프로젝트가 2025년 3월에 성공적으로 완료되었습니다',
        '다음 프로젝트는 2025년 11월에 착수되어 상용 규모 IsoTES® 보급을 목표로 합니다',
        'KOICA가 두 프로젝트를 모두 지원하고 있습니다'
      ]
    },
    images: {
      diagram: {
        src: '/images/applications/residential-heating-overview.webp',
        caption: {
          en: 'Transition from coal-based heating to IsoTES® clean heating system',
          ko: '석탄 기반 난방에서 IsoTES® 청정 난방 시스템으로 전환'
        }
      }
    },
    merits: {
      en: [
        'No coal (purchase, transport, waste, pollution, tax)',
        'Full automation',
        'Negligible OPEX'
      ],
      ko: [
        '석탄 불필요 (구매, 운송, 폐기물, 오염, 세금)',
        '완전 자동화',
        '무시할 수준의 운영비'
      ]
    },
    relatedProject: {
      title: 'KOICA CTS Seed 1',
      href: '/projects/koica-cts-seed-1'
    }
  },

  // ============================================
  // GRID APPLICATIONS
  // ============================================
  {
    id: 'wind-power-storage',
    slug: 'wind-power-storage',
    category: 'grid',
    title: {
      en: 'Storing Curtailed Wind Power for Continuous Supply',
      ko: '연속 공급을 위한 풍력 잉여 전력 저장'
    },
    shortTitle: {
      en: 'Wind Power Continuity',
      ko: '풍력 연속 공급'
    },
    tagline: {
      en: 'Capture curtailed wind energy to ensure uninterrupted power supply',
      ko: '잉여 풍력 에너지를 포집하여 중단 없는 전력 공급 보장'
    },
    description: {
      en: 'Korea is becoming a leading offshore wind power country with 2 GW offshore wind power purchase planned for December 2025. IsoTES® serves as ESS for offshore wind, storing curtailed power as heat and converting it back to electricity to guarantee minimum power supply.',
      ko: '한국은 2025년 12월 2GW 해상풍력 전력 구매가 계획된 해상풍력 선도 국가로 부상하고 있습니다. IsoTES®는 해상풍력용 ESS로서 잉여 전력을 열로 저장하고 다시 전기로 변환하여 최소 전력 공급을 보장합니다.'
    },
    icon: '💨',
    temperatureRange: '280-565°C',
    keyFeatures: {
      en: [
        'Power-to-heat conversion at scale (5,000 MW offshore wind turbines)',
        'Prismatic thermocline tank with electric heater',
        'Steam turbine generator for electricity output',
        'Can be installed on used ships (e.g., 100K oil tanker for 10 GWh)'
      ],
      ko: [
        '대규모 전력-열 변환 (5,000 MW 해상 풍력터빈)',
        '전기 히터가 있는 프리즈매틱 서모클라인 탱크',
        '전력 출력용 증기터빈 발전기',
        '중고선에 설치 가능 (예: 10 GWh용 100K 유조선)'
      ]
    },
    benefits: {
      en: [
        'Guarantee minimum wind power supply',
        'Store and utilize curtailed wind power',
        'Grid-scale energy storage solution',
        'Lower LCOS with glycerin-based TESM'
      ],
      ko: [
        '최소 풍력 전력 공급 보장',
        '잉여 풍력 저장 및 활용',
        '계통 규모 에너지 저장 솔루션',
        '글리세린 기반 TESM으로 낮은 LCOS'
      ]
    },
    technicalHighlights: {
      en: [
        'Maximum temperature: 565°C (molten salt) / 280°C (glycerin)',
        'Cycle efficiency: 40% (molten salt) / 20% (glycerin)',
        'Equipment: ASTM SS347 (molten salt) / ASTM A36, A53 (glycerin)'
      ],
      ko: [
        '최대 온도: 565°C (용융염) / 280°C (글리세린)',
        '사이클 효율: 40% (용융염) / 20% (글리세린)',
        '장비: ASTM SS347 (용융염) / ASTM A36, A53 (글리세린)'
      ]
    },
    relatedTechnology: ['HTS IsoTES®', 'Charge/Discharge', 'TESM'],
    background: [
      {
        title: {
          en: 'Korea is becoming a leading offshore wind power country',
          ko: '한국은 해상풍력 선도 국가로 부상하고 있습니다'
        },
        items: {
          en: [
            'Korea has nominated suppliers for 2 GW offshore wind power purchase in December 2025',
            'Offshore wind is one of the key renewable energy sources with varying output'
          ],
          ko: [
            '한국은 2025년 12월 2GW 해상풍력 전력 구매 공급자를 선정했습니다',
            '해상풍력은 출력 변동이 있는 주요 재생에너지원 중 하나입니다'
          ]
        }
      }
    ],
    challenges: [
      {
        title: {
          en: 'Offshore wind power faces key challenges',
          ko: '해상풍력은 핵심 과제에 직면해 있습니다'
        },
        items: {
          en: [
            'How to guarantee minimum wind power supply',
            'How to handle curtailed wind power when supply exceeds demand'
          ],
          ko: [
            '최소 풍력 전력 공급을 어떻게 보장할 것인가',
            '공급이 수요를 초과할 때 잉여 풍력을 어떻게 처리할 것인가'
          ]
        }
      }
    ],
    solutionValues: {
      en: [
        'IsoTES® can be the ESS for offshore wind',
        'IsoTES® stores curtailed wind power in the form of heat and converts it to electricity',
        'This guarantees the minimum power supply from wind farms',
        'The IsoTES® is huge in size for this application - a 10 GWh system needs about 60,000 m³ thermal storage tanks',
        'We are thinking about installing IsoTES® on used ships (e.g., 100K oil tanker)'
      ],
      ko: [
        'IsoTES®는 해상풍력용 ESS가 될 수 있습니다',
        'IsoTES®는 잉여 풍력을 열의 형태로 저장하고 전기로 변환합니다',
        '이를 통해 풍력 발전소의 최소 전력 공급을 보장합니다',
        '이 용도의 IsoTES®는 크기가 매우 큽니다 - 10 GWh 시스템에 약 60,000 m³ 열저장 탱크가 필요합니다',
        '중고선에 IsoTES® 설치를 검토 중입니다 (예: 100K 유조선)'
      ]
    },
    statusPlan: {
      en: [
        'GIGAette and KAIST have conducted a feasibility study comparing two TESM options: Glycerin and Molten salts',
        'Though glycerin delivers lower temperature heat, it is regarded as better TESM considering the LCOS and solidification risk',
        'Glycerin is liquid at room temperature while molten salt solidifies, avoiding costly heat tracing systems'
      ],
      ko: [
        'GIGAette와 KAIST가 두 가지 TESM 옵션(글리세린과 용융염)을 비교하는 타당성 연구를 수행했습니다',
        '글리세린이 더 낮은 온도의 열을 전달하지만 LCOS와 응고 위험을 고려할 때 더 나은 TESM으로 평가됩니다',
        '글리세린은 상온에서 액체이고 용융염은 응고되어 비용이 많이 드는 열 추적 시스템을 피할 수 있습니다'
      ]
    },
    images: {
      overview: {
        src: '/images/applications/curtailed-wind-power-overview.webp',
        caption: {
          en: 'Offshore wind power storage concept with IsoTES®',
          ko: 'IsoTES®를 활용한 해상풍력 저장 개념'
        }
      },
      diagram: {
        src: '/images/applications/curtailed-wind-power-conceptual-design.webp',
        caption: {
          en: 'Process flow diagram of the conceptual design: Offshore wind turbines → Electric heater → Prismatic thermocline tank → Steam turbine → Grid',
          ko: '개념 설계의 공정 흐름도: 해상 풍력터빈 → 전기 히터 → 프리즈매틱 서모클라인 탱크 → 증기터빈 → 계통'
        }
      }
    },
    tesmComparison: {
      title: {
        en: 'Comparison of Molten Salt and Glycerin-based System',
        ko: '용융염과 글리세린 기반 시스템 비교'
      },
      items: [
        {
          property: { en: 'Storage Temperature', ko: '저장 온도' },
          glycerin: { en: '280°C (Lower)', ko: '280°C (낮음)' },
          moltenSalt: { en: '565°C (Higher)', ko: '565°C (높음)' }
        },
        {
          property: { en: 'Carnot Efficiency', ko: '카르노 효율' },
          glycerin: { en: '20% (Lower)', ko: '20% (낮음)' },
          moltenSalt: { en: '40% (Higher)', ko: '40% (높음)' }
        },
        {
          property: { en: 'Storage Tank Size', ko: '저장 탱크 크기' },
          glycerin: { en: 'Bigger', ko: '더 큼' },
          moltenSalt: { en: 'Smaller', ko: '더 작음' }
        },
        {
          property: { en: 'Tank Material Cost', ko: '탱크 재료 비용' },
          glycerin: { en: 'Cheaper (Lower CAPEX)', ko: '더 저렴 (낮은 CAPEX)' },
          moltenSalt: { en: 'More expensive', ko: '더 비쌈' },
          isHighlighted: true
        },
        {
          property: { en: 'State at Room Temp', ko: '상온 상태' },
          glycerin: { en: 'Liquid (No solidification risk)', ko: '액체 (응고 위험 없음)' },
          moltenSalt: { en: 'Solidifies (Requires heat tracing)', ko: '응고됨 (열 추적 필요)' },
          isHighlighted: true
        },
        {
          property: { en: 'Solidification Risk', ko: '응고 위험' },
          glycerin: { en: 'None', ko: '없음' },
          moltenSalt: { en: 'Pugging, thermal expansion damage', ko: '플러깅, 열팽창 손상' },
          isHighlighted: true
        },
        {
          property: { en: 'Maintenance', ko: '유지보수' },
          glycerin: { en: 'Easier', ko: '더 쉬움' },
          moltenSalt: { en: 'More complex', ko: '더 복잡' },
          isHighlighted: true
        },
        {
          property: { en: 'LCOS Difference', ko: 'LCOS 차이' },
          glycerin: { en: 'Comparable', ko: '유사함' },
          moltenSalt: { en: 'Comparable', ko: '유사함' }
        }
      ],
      conclusion: {
        en: 'Glycerin is better in terms of solidification-induced risk and cost, with insignificant difference in LCOS between the two options.',
        ko: '글리세린은 응고로 인한 위험과 비용 측면에서 더 우수하며, 두 옵션 간 LCOS 차이는 미미합니다.'
      }
    }
  },
  {
    id: 'coal-plant-conversion',
    slug: 'coal-plant-conversion',
    category: 'grid',
    title: {
      en: 'Conversion of Retired Coal-Fired Power Plant to Grid ESS',
      ko: '퇴역 석탄화력발전소의 계통용 ESS 전환'
    },
    shortTitle: {
      en: 'Coal Plant to Grid Storage',
      ko: '석탄발전소 계통 저장 전환'
    },
    tagline: {
      en: 'Transform retired coal plants into grid-scale thermal energy storage facilities',
      ko: '퇴역 석탄발전소를 계통 규모 열에너지 저장 시설로 전환'
    },
    description: {
      en: 'Retired coal-fired power plants have valuable existing infrastructure including grid connections, turbines, and cooling systems. IsoTES® enables conversion of these facilities into grid-scale energy storage systems by replacing the boiler with thermal energy storage. The existing turbines and generators continue to produce dispatchable power, creating new life for stranded assets while preserving jobs and guaranteeing stable power supply.',
      ko: '퇴역 석탄화력발전소는 계통 연결, 터빈, 냉각 시스템을 포함한 가치 있는 기존 인프라를 보유하고 있습니다. IsoTES®는 보일러를 열에너지 저장으로 대체하여 이 시설을 계통 규모 에너지 저장 시스템으로 전환합니다. 기존 터빈과 발전기가 계속 전력을 생산하여 좌초 자산에 새 생명을 불어넣고 일자리를 보전하며 안정적인 전력 공급을 보장합니다.'
    },
    icon: '🏗️',
    temperatureRange: '400-600°C',
    keyFeatures: {
      en: [
        'Utilize existing power plant infrastructure',
        'Grid-scale thermal storage integration',
        'Existing turbine and generator reuse',
        'Transmission infrastructure leverage'
      ],
      ko: [
        '기존 발전소 인프라 활용',
        '계통 규모 열저장 통합',
        '기존 터빈 및 발전기 재사용',
        '송전 인프라 활용'
      ]
    },
    benefits: {
      en: [
        'Repurpose stranded coal plant assets',
        'Maintain local employment',
        'Provide grid-scale storage at lower cost',
        'Accelerate energy transition'
      ],
      ko: [
        '좌초 석탄발전 자산 재활용',
        '지역 고용 유지',
        '저비용 계통 규모 저장 제공',
        '에너지 전환 가속화'
      ]
    },
    technicalHighlights: {
      en: [
        'Large-scale molten salt storage',
        'Steam system integration',
        'Grid services capability'
      ],
      ko: [
        '대규모 용융염 저장',
        '증기 시스템 통합',
        '계통 서비스 역량'
      ]
    },
    relatedTechnology: ['HTS IsoTES®', 'Standard/Custom'],
    background: [
      {
        title: {
          en: 'Retired coal-fired power plants are causing a lot of issues',
          ko: '퇴역 석탄화력발전소는 많은 문제를 야기하고 있습니다'
        },
        items: {
          en: [
            'Lost jobs for power plant workers and local communities',
            'Asset damage from stranded infrastructure investments',
            'Unstable power supply as reliable baseload capacity is retired'
          ],
          ko: [
            '발전소 근로자와 지역 사회의 일자리 상실',
            '좌초 인프라 투자로 인한 자산 손실',
            '신뢰할 수 있는 기저부하 용량 퇴역으로 전력 공급 불안정'
          ]
        }
      },
      {
        title: {
          en: 'Korea is no exception',
          ko: '한국도 예외는 아닙니다'
        },
        items: {
          en: [
            'Korea has to retire 28 coal-fired power plants by 2036',
            'This represents significant economic and employment challenges'
          ],
          ko: [
            '한국은 2036년까지 28개 석탄화력발전소를 퇴역시켜야 합니다',
            '이는 상당한 경제적, 고용 문제를 의미합니다'
          ]
        }
      }
    ],
    challenges: [
      {
        title: {
          en: 'How to convert retired coal plants while preserving value',
          ko: '가치를 보전하면서 퇴역 석탄발전소를 전환하는 방법'
        },
        items: {
          en: [
            'Need to maintain employment for local communities',
            'Must preserve valuable grid infrastructure investments',
            'Require stable power supply to replace retired capacity'
          ],
          ko: [
            '지역 사회 고용 유지 필요',
            '가치 있는 계통 인프라 투자 보전 필수',
            '퇴역 용량을 대체할 안정적인 전력 공급 필요'
          ]
        }
      }
    ],
    solutionValues: {
      en: [
        'If the boiler of a retired coal-fired power plant is replaced by the IsoTES®, it can be converted into a grid-scale ESS',
        'The other parts of the plant can be used without modification',
        'It will remove the issues caused by the retired power plants'
      ],
      ko: [
        '퇴역 석탄화력발전소의 보일러를 IsoTES®로 대체하면 계통 규모 ESS로 전환할 수 있습니다',
        '발전소의 다른 부분은 수정 없이 사용할 수 있습니다',
        '퇴역 발전소로 인한 문제를 해결할 수 있습니다'
      ]
    },
    statusPlan: {
      en: [
        'GIGAette and KAIST have conducted a feasibility study as shown in the comparison table',
        'The study proves that the solution is very cost effective',
        'This approach preserves jobs and assets, while guaranteeing stable power supply'
      ],
      ko: [
        'GIGAette와 KAIST가 비교표에 제시된 타당성 연구를 수행했습니다',
        '연구 결과 이 솔루션이 매우 비용 효율적임이 입증되었습니다',
        '이 접근법은 일자리와 자산을 보전하면서 안정적인 전력 공급을 보장합니다'
      ]
    },
    beforeAfterComparison: {
      before: {
        src: '/images/applications/standard-coal-fire-power-plant.webp',
        title: {
          en: '500MW Standard Coal-Fire Power Plant',
          ko: '500MW 표준 석탄화력발전소'
        },
        citation: {
          source: '한국전력기술',
          url: 'https://www.kepco-enc.com/portal/contents.do?key=1441',
          reference: 'Jae-Heon JEON, Design of Thermal Energy Storage System Based on Molten Salts for Supercritical Steam Power Plants, Master Thesis, Department of Mechanical Eng., KAIST (2023)'
        }
      },
      after: {
        src: '/images/applications/converted-coal-fire-power-plant.webp',
        title: {
          en: '500 MW Grid-Scale ESS',
          ko: '500 MW 계통 규모 ESS'
        },
        caption: {
          en: 'IsoTES® converts the coal-fire power plant to a grid ESS.',
          ko: 'IsoTES®가 석탄화력발전소를 계통용 ESS로 전환합니다.'
        }
      }
    },
    essComparison: {
      title: {
        en: 'Comparison of Grid-Scale ESS Technologies',
        ko: '계통 규모 ESS 기술 비교'
      },
      rows: [
        { essType: 'Lithium-ion battery (LIB)', storageCapacity: '1 ~ 1,200', power: '0 ~ 100', energyDensity: '250 ~ 500', roundTripEfficiency: '0.75 ~ 0.95', lcos: '0.27 ~ 0.42', responseTime: '< 1 sec', supplyEnergy: { en: 'electricity', ko: '전기' } },
        { essType: 'Compressed air (CAES)', storageCapacity: '10 ~ 1,000', power: '5 ~ 300', energyDensity: '3 ~ 6', roundTripEfficiency: '0.70 ~ 0.79', lcos: '0.12 ~ 0.16', responseTime: '< 15 min', supplyEnergy: { en: 'electricity', ko: '전기' } },
        { essType: 'Liquified air (LAES)', storageCapacity: '25 ~ 1,200', power: '10 ~ 200', energyDensity: '120 ~ 200', roundTripEfficiency: '0.50 ~ 0.60', lcos: '0.24 ~ 0.75', responseTime: 'min', supplyEnergy: { en: 'electricity, cold energy', ko: '전기, 냉열' } },
        { essType: 'Pumped hydro (PHS)', storageCapacity: '500 ~ 8,000', power: '5 ~ 1,000', energyDensity: '0.1 ~ 1.5', roundTripEfficiency: '0.80 ~ 0.87', lcos: '0.10 ~ 0.13', responseTime: 'sec ~ min', supplyEnergy: { en: 'electricity', ko: '전기' } },
        { essType: 'IsoTES®', storageCapacity: '5 ~ 15,000', power: '1 ~ 500', energyDensity: '100 ~ 250', roundTripEfficiency: '0.40 ~ 0.44', lcos: '0.14 ~ 0.25', responseTime: 'min', supplyEnergy: { en: 'electricity, heat', ko: '전기, 열' }, isHighlighted: true }
      ]
    }
  },

  // ============================================
  // SMR APPLICATIONS
  // ============================================
  {
    id: 'smr-power-plant',
    slug: 'smr-power-plant',
    category: 'smr',
    title: {
      en: 'Surplus Heat Storage from SMR Power Plants',
      ko: 'SMR 발전소 잉여열 저장'
    },
    shortTitle: {
      en: 'SMR Power Plant Heat Storage',
      ko: 'SMR 발전소 열 저장'
    },
    tagline: {
      en: 'Store excess heat from Small Modular Reactors for grid flexibility',
      ko: '계통 유연성을 위해 소형모듈원자로의 잉여열 저장'
    },
    description: {
      en: 'Small Modular Reactors (SMRs) operate most efficiently at constant output, but grid demand fluctuates. IsoTES® stores surplus heat during low-demand periods and releases it during peak demand, enabling SMRs to operate at optimal efficiency while providing grid flexibility. This thermal storage solution maximizes the value of nuclear energy and supports grid stability.',
      ko: '소형모듈원자로(SMR)는 일정한 출력에서 가장 효율적으로 운전되지만 계통 수요는 변동합니다. IsoTES®는 수요가 낮은 기간에 잉여열을 저장하고 피크 수요 시 방출하여 SMR이 최적 효율로 운전하면서 계통 유연성을 제공할 수 있게 합니다. 이 열저장 솔루션은 원자력 에너지의 가치를 극대화하고 계통 안정성을 지원합니다.'
    },
    icon: '⚛️',
    temperatureRange: '300-550°C',
    keyFeatures: {
      en: [
        'High-temperature heat capture from SMR',
        'Load-following capability for nuclear',
        'Peak shaving and load shifting',
        'Integration with existing steam systems'
      ],
      ko: [
        'SMR에서 고온 열 회수',
        '원자력 부하 추종 기능',
        '피크 절감 및 부하 이동',
        '기존 증기 시스템과 통합'
      ]
    },
    benefits: {
      en: [
        'Enable SMR load-following operation',
        'Maximize nuclear plant revenue',
        'Provide grid stability services',
        'Improve overall plant economics'
      ],
      ko: [
        'SMR 부하 추종 운전 가능',
        '원전 수익 극대화',
        '계통 안정화 서비스 제공',
        '전반적인 발전소 경제성 향상'
      ]
    },
    technicalHighlights: {
      en: [
        'Nuclear-grade heat exchangers',
        'Molten salt thermal storage',
        'Safety system integration'
      ],
      ko: [
        '원자력급 열교환기',
        '용융염 열저장',
        '안전 시스템 통합'
      ]
    },
    relatedTechnology: ['HTS IsoTES®', 'TESM'],
    background: [
      {
        title: {
          en: 'Conventional Two-Tank Thermal Storage Systems',
          ko: '기존 2탱크 열저장 시스템'
        },
        items: {
          en: [
            'The surplus heat from SMR power plants is usually stored by a two-tank molten salt thermal energy storage system'
          ],
          ko: [
            'SMR 발전소의 잉여열은 일반적으로 2탱크 용융염 열에너지 저장 시스템에 저장됩니다'
          ]
        }
      }
    ],
    challenges: [
      {
        title: {
          en: 'Two-Tank System Fatigue Issues',
          ko: '2탱크 시스템 피로 문제'
        },
        items: {
          en: [
            'One critical issue of this solution is that the two tanks are subjected to daily repeated loads as the liquid level of the two tanks changes from bottom to top as the molten salt is circulated between the two tanks',
            'This repeated load can result in fatigue damage on the tanks, causing serious safety concern of the SMR power plants'
          ],
          ko: [
            '이 솔루션의 핵심 문제는 두 탱크 사이에 용융염이 순환하면서 액위가 바닥에서 상단으로 변하기 때문에 두 탱크가 일일 반복 하중을 받는다는 것입니다',
            '이 반복 하중은 탱크에 피로 손상을 일으켜 SMR 발전소의 심각한 안전 우려를 야기할 수 있습니다'
          ]
        }
      }
    ],
    solutionValues: {
      en: [
        'The IsoTES® is a one tank solution, that is, the liquid level of the tank remains constant',
        'So, the tank is free from repeated load and consequential fatigue damage'
      ],
      ko: [
        'IsoTES®는 단일 탱크 솔루션으로 탱크의 액위가 일정하게 유지됩니다',
        '따라서 탱크는 반복 하중과 그로 인한 피로 손상으로부터 자유롭습니다'
      ]
    },
    statusPlan: {
      en: [
        'An international collaboration study is under formulation'
      ],
      ko: [
        '국제 협력 연구가 수립 중입니다'
      ]
    },
    images: {
      diagram: {
        src: '/images/applications/smr-power-plants-overview.webp',
        caption: {
          en: 'IsoTES® thermal energy storage integration with SMR power plants for surplus steam capture and grid flexibility',
          ko: '잉여 증기 포집과 계통 유연성을 위한 SMR 발전소와 IsoTES® 열에너지 저장 통합'
        }
      }
    },
    smrTesmComparison: {
      title: {
        en: 'Thermal Energy Storage Material (TESM) for SMR',
        ko: 'SMR용 열에너지 저장 물질(TESM)'
      },
      rows: [
        { smr: 'Holtec SMR-300', reactorType: 'PWR', heatSourceTemp: '315', tesm: { en: 'Glycerin', ko: '글리세린' } },
        { smr: 'NuScale', reactorType: 'PWR', heatSourceTemp: '300', tesm: { en: 'Glycerin', ko: '글리세린' } },
        { smr: 'GE BWRX-300', reactorType: 'BWR', heatSourceTemp: '285', tesm: { en: 'Glycerin', ko: '글리세린' } },
        { smr: 'X-energy Xe-100', reactorType: 'HTGR', heatSourceTemp: '750', tesm: { en: 'Molten salts', ko: '용융염' } },
        { smr: 'TerraPower Natrium', reactorType: 'SFR + TES', heatSourceTemp: '550', tesm: { en: 'Molten salts', ko: '용융염' } },
        { smr: 'Seaborg', reactorType: 'MSR', heatSourceTemp: '650', tesm: { en: 'Solar salts', ko: '솔라 솔트' } },
        { smr: 'USNC MMR', reactorType: 'HTGR', heatSourceTemp: '700', tesm: { en: 'Molten salts', ko: '용융염' } }
      ],
      caption: {
        en: 'SMR: Small Modular Reactor (소형모듈형 원자로) · TES: Thermal Energy Storage (열에너지 저장 시스템) · PWR: Pressurized Water Reactor (가압경수로) · BWR: Boiling Water Reactor (비등경수로) · HTGR: High Temperature Gas-cooled Reactor (고온가스로) · SFR: Sodium-cooled Fast Reactor (소듐냉각고속로) · MSR: Molten Salt Reactor (용융염로)',
        ko: 'SMR: 소형모듈형 원자로 · TES: 열에너지 저장 시스템 · PWR: 가압경수로 · BWR: 비등경수로 · HTGR: 고온가스로 · SFR: 소듐냉각고속로 · MSR: 용융염로'
      }
    }
  },
  {
    id: 'smr-ship-port',
    slug: 'smr-ship-port',
    category: 'smr',
    title: {
      en: 'Surplus Heat Storage from SMR-Powered Ships at Ports',
      ko: '항구 정박 SMR 추진 선박의 잉여열 저장'
    },
    shortTitle: {
      en: 'SMR Ship Heat at Ports',
      ko: '항구 SMR 선박 열'
    },
    tagline: {
      en: 'Capture heat from nuclear-powered ships to supply port energy needs',
      ko: '원자력 추진 선박의 열을 포집하여 항구 에너지 수요에 공급'
    },
    description: {
      en: 'Nuclear-powered ships generate continuous heat even when docked at ports. IsoTES® captures this surplus heat and stores it for port operations, including cold storage facilities, building heating, and industrial processes. This innovative application creates value from otherwise wasted energy while reducing port emissions and energy costs.',
      ko: '원자력 추진 선박은 항구에 정박해 있을 때도 지속적으로 열을 발생시킵니다. IsoTES®는 이 잉여열을 포집하고 냉동 창고, 건물 난방, 산업 공정 등 항구 운영에 저장합니다. 이 혁신적인 응용은 버려지는 에너지로부터 가치를 창출하고 항구 배출과 에너지 비용을 줄입니다.'
    },
    icon: '⚓',
    temperatureRange: '150-400°C',
    keyFeatures: {
      en: [
        'Ship-to-shore heat transfer system',
        'Port-side thermal storage',
        'Multiple end-use applications',
        'Flexible discharge capability'
      ],
      ko: [
        '선박-육지 열전달 시스템',
        '항만 측 열저장',
        '다중 최종 사용 응용',
        '유연한 방열 기능'
      ]
    },
    benefits: {
      en: [
        'Utilize ship waste heat during port stays',
        'Reduce port energy costs',
        'Lower port emissions',
        'Create new revenue for ship operators'
      ],
      ko: [
        '항구 정박 시 선박 폐열 활용',
        '항구 에너지 비용 절감',
        '항구 배출 저감',
        '선박 운영자에게 새로운 수익 창출'
      ]
    },
    technicalHighlights: {
      en: [
        'Marine-grade connection systems',
        'Multi-temperature storage',
        'Port infrastructure integration'
      ],
      ko: [
        '선박용 연결 시스템',
        '다중 온도 저장',
        '항만 인프라 통합'
      ]
    },
    relatedTechnology: ['Hybrid IsoTES®', 'TESM'],
    background: [
      {
        title: {
          en: 'IMO Net Zero Initiative',
          ko: 'IMO 넷제로 이니셔티브'
        },
        items: {
          en: [
            'All international ships are regulated by IMO (International Maritime Organization) under UN',
            'IMO declared the plan to achieve Net Zero of international shipping by 2050',
            'Many zero-emission fuels are considered: hydrogen, methanol, ammonia, etc. None of them turns out to be financially affordable because they are extremely expensive'
          ],
          ko: [
            '모든 국제 선박은 UN 산하 IMO(국제해사기구)의 규제를 받습니다',
            'IMO는 2050년까지 국제 해운의 넷제로 달성 계획을 선언했습니다',
            '수소, 메탄올, 암모니아 등 다양한 무배출 연료가 검토되고 있지만 비용이 매우 비싸 경제적으로 감당하기 어렵습니다'
          ]
        }
      },
      {
        title: {
          en: 'SMR Propulsion Interest',
          ko: 'SMR 추진 관심'
        },
        items: {
          en: [
            'Propulsion by SMR is proved to be economically attractive, and many ship owners are raising serious interest',
            'However, non-technical challenges such as handling of nuclear fuel have emerged as the barriers. To tackle these challenges IAEA launched "ATLAS (Atomic Technology Licensed for Applications at Sea)"'
          ],
          ko: [
            'SMR 추진이 경제적으로 매력적인 것으로 입증되어 많은 선주가 진지한 관심을 보이고 있습니다',
            '그러나 핵연료 취급 등 비기술적 과제가 장벽으로 부상하고 있습니다. 이러한 과제를 해결하기 위해 IAEA는 "ATLAS(해양 응용 허가 원자력 기술)"를 출범시켰습니다'
          ]
        }
      }
    ],
    challenges: [
      {
        title: {
          en: 'Minimum Heat Release at Port',
          ko: '항구에서의 최소 열 방출'
        },
        items: {
          en: [
            'One big issue is how to handle the minimum heat released from the SMR when the ship is at port with no propulsion load'
          ],
          ko: [
            '핵심 문제는 선박이 추진 부하 없이 항구에 있을 때 SMR에서 방출되는 최소 열을 어떻게 처리하느냐는 것입니다'
          ]
        }
      }
    ],
    solutionValues: {
      en: [
        'The IsoTES® can store the minimum heat from the SMR',
        'The heat is released by generating steam that is used for propulsion for the sail on open sea',
        'This solution can prevent heat release from the ship at port and utilize the energy that can be lost otherwise'
      ],
      ko: [
        'IsoTES®는 SMR의 최소 열을 저장할 수 있습니다',
        '열은 증기를 발생시켜 공해에서 항해 추진에 사용됩니다',
        '이 솔루션은 항구에서 선박의 열 방출을 방지하고 손실될 수 있는 에너지를 활용할 수 있습니다'
      ]
    },
    statusPlan: {
      en: [
        'An industrial joint project is under formulation'
      ],
      ko: [
        '산업 공동 프로젝트가 수립 중입니다'
      ]
    },
    images: {
      diagram: {
        src: '/images/applications/smr-powered-ships.webp',
        caption: {
          en: 'IsoTES® thermal energy storage system for SMR-powered ships at port',
          ko: '항구 정박 SMR 추진 선박을 위한 IsoTES® 열에너지 저장 시스템'
        }
      }
    }
  }
]

/**
 * Application categories with metadata (bilingual)
 */
const applicationCategoriesData: ApplicationCategoryData[] = [
  {
    id: 'industrial',
    slug: 'industrial',
    title: { en: 'Industrial', ko: '산업용' },
    description: {
      en: 'High-temperature thermal storage solutions for manufacturing, processing, and heavy industry applications.',
      ko: '제조, 가공 및 중공업 응용을 위한 고온 열저장 솔루션.'
    },
    icon: '🏭'
  },
  {
    id: 'residential',
    slug: 'residential',
    title: { en: 'Residential', ko: '주거용' },
    description: {
      en: 'Clean, efficient heating solutions for homes, communities, and district heating networks.',
      ko: '가정, 커뮤니티 및 지역난방 네트워크를 위한 청정 고효율 난방 솔루션.'
    },
    icon: '🏠'
  },
  {
    id: 'grid',
    slug: 'grid',
    title: { en: 'Grid', ko: '계통용' },
    description: {
      en: 'Grid-scale energy storage for renewable integration, load balancing, and power plant transformation.',
      ko: '재생에너지 통합, 부하 균형 및 발전소 전환을 위한 계통 규모 에너지 저장.'
    },
    icon: '⚡'
  },
  {
    id: 'smr',
    slug: 'smr',
    title: { en: 'SMR', ko: 'SMR' },
    description: {
      en: 'Thermal energy storage solutions optimized for Small Modular Reactor applications.',
      ko: '소형모듈원자로 응용에 최적화된 열에너지 저장 솔루션.'
    },
    icon: '⚛️'
  }
]

/**
 * Convert bilingual case data to localized case
 */
function localizeCase(data: ApplicationCaseData): ApplicationCase {
  return {
    id: data.id,
    slug: data.slug,
    category: data.category,
    title: localizedText(data.title),
    shortTitle: localizedText(data.shortTitle),
    tagline: localizedText(data.tagline),
    description: localizedText(data.description),
    icon: data.icon,
    temperatureRange: data.temperatureRange,
    keyFeatures: localizedArray(data.keyFeatures),
    benefits: localizedArray(data.benefits),
    technicalHighlights: data.technicalHighlights ? localizedArray(data.technicalHighlights) : undefined,
    relatedTechnology: data.relatedTechnology,
    background: data.background?.map(b => ({
      title: localizedText(b.title),
      items: localizedArray(b.items)
    })),
    challenges: data.challenges?.map(c => ({
      title: localizedText(c.title),
      items: localizedArray(c.items)
    })),
    solutionValues: data.solutionValues ? localizedArray(data.solutionValues) : undefined,
    statusPlan: data.statusPlan ? localizedArray(data.statusPlan) : undefined,
    images: data.images ? {
      before: data.images.before ? { src: data.images.before.src, caption: localizedText(data.images.before.caption) } : undefined,
      after: data.images.after ? { src: data.images.after.src, caption: localizedText(data.images.after.caption) } : undefined,
      diagram: data.images.diagram ? { src: data.images.diagram.src, caption: localizedText(data.images.diagram.caption) } : undefined,
      overview: data.images.overview ? { src: data.images.overview.src, caption: localizedText(data.images.overview.caption) } : undefined
    } : undefined,
    merits: data.merits ? localizedArray(data.merits) : undefined,
    costComparison: data.costComparison ? {
      title: data.costComparison.title ? localizedText(data.costComparison.title) : undefined,
      unit: data.costComparison.unit,
      rows: data.costComparison.rows
    } : undefined,
    tesmComparison: data.tesmComparison ? {
      title: data.tesmComparison.title ? localizedText(data.tesmComparison.title) : undefined,
      items: data.tesmComparison.items.map(item => ({
        property: localizedText(item.property),
        glycerin: localizedText(item.glycerin),
        moltenSalt: localizedText(item.moltenSalt),
        isHighlighted: item.isHighlighted
      })),
      conclusion: data.tesmComparison.conclusion ? localizedText(data.tesmComparison.conclusion) : undefined
    } : undefined,
    essComparison: data.essComparison ? {
      title: data.essComparison.title ? localizedText(data.essComparison.title) : undefined,
      rows: data.essComparison.rows.map(row => ({
        ...row,
        supplyEnergy: localizedText(row.supplyEnergy)
      }))
    } : undefined,
    beforeAfterComparison: data.beforeAfterComparison ? {
      before: {
        src: data.beforeAfterComparison.before.src,
        title: localizedText(data.beforeAfterComparison.before.title),
        citation: data.beforeAfterComparison.before.citation
      },
      after: {
        src: data.beforeAfterComparison.after.src,
        title: localizedText(data.beforeAfterComparison.after.title),
        caption: data.beforeAfterComparison.after.caption ? localizedText(data.beforeAfterComparison.after.caption) : undefined
      }
    } : undefined,
    smrTesmComparison: data.smrTesmComparison ? {
      title: data.smrTesmComparison.title ? localizedText(data.smrTesmComparison.title) : undefined,
      rows: data.smrTesmComparison.rows.map(row => ({
        smr: row.smr,
        reactorType: row.reactorType,
        heatSourceTemp: row.heatSourceTemp,
        tesm: localizedText(row.tesm)
      })),
      caption: data.smrTesmComparison.caption ? localizedText(data.smrTesmComparison.caption) : undefined
    } : undefined,
    relatedProject: data.relatedProject,
    comingSoon: data.comingSoon
  }
}

/**
 * Convert bilingual category data to localized category
 */
function localizeCategory(data: ApplicationCategoryData): ApplicationCategory {
  const cases = applicationCasesData
    .filter(c => c.category === data.slug)
    .map(localizeCase)

  return {
    id: data.id,
    slug: data.slug,
    title: localizedText(data.title),
    description: localizedText(data.description),
    icon: data.icon,
    cases
  }
}

/**
 * Get all application cases (localized)
 */
export function getApplicationCases(): ApplicationCase[] {
  return applicationCasesData.map(localizeCase)
}

/**
 * Get all cases for a specific category (localized)
 */
export function getCasesByCategory(categorySlug: string): ApplicationCase[] {
  return applicationCasesData
    .filter(c => c.category === categorySlug)
    .map(localizeCase)
}

/**
 * Get a specific case by slug (localized)
 */
export function getCaseBySlug(slug: string): ApplicationCase | undefined {
  const data = applicationCasesData.find(c => c.slug === slug)
  return data ? localizeCase(data) : undefined
}

/**
 * Get category by slug (localized)
 */
export function getCategoryBySlug(slug: string): ApplicationCategory | undefined {
  const data = applicationCategoriesData.find(c => c.slug === slug)
  return data ? localizeCategory(data) : undefined
}

/**
 * Get all categories (localized)
 */
export function getAllCategories(): ApplicationCategory[] {
  return applicationCategoriesData.map(localizeCategory)
}

// Export legacy arrays for backward compatibility (these are now functions)
export const applicationCases = getApplicationCases()
export const applicationCategories = getAllCategories()
